import { useMemo } from 'react';

import { useAvailableShareChannels } from '../availableChannels';
import type { ShareChannel, ShareConfig } from '../shared/types';
import { useGetShareShortUrl } from '../urls';

import type { ShareAction } from './actions';
import { useShareActions } from './actions';

export type Options<CH extends ShareChannel = ShareChannel> = {
	config?: ShareConfig;
	disableShortLinks?: boolean;
	trackingEventName: string;
	getUrl: (channel: CH) => string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

export function useAvailableShareActions<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	{
		getUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		disableShortLinks,
		onSuccess,
		onFailure,
		config,
	}: Options<CH>,
): ReadonlyArray<ShareAction<CH>> {
	const configWithDefaults = useMemo(() => config || {}, [config]);

	const availableChannels = useAvailableShareChannels(channels, configWithDefaults);

	const getShortUrl = useGetShareShortUrl(availableChannels, { getUrl, disableShortLinks });

	return useShareActions(availableChannels, {
		getUrl,
		getShortUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		onSuccess,
		onFailure,
		config: configWithDefaults,
	});
}

export type { ShareAction };
